import {
  AnalyticsProps,
  NOT_TRACKED_CONTENT,
} from '../../../InternalLibrary/analytics';
import { useAnalytics } from 'use-analytics';
import classes from 'classnames';
import countryList from 'utils/countryList.json';
import iban from 'iban';
import InputMask from 'react-input-mask';
import layout from 'scss/layout.module.scss';
import React, {
  ClipboardEventHandler,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './IBAN.module.scss';

interface Props extends AnalyticsProps {
  countryISO: string;
  onChange?: (iban: string, isValid: boolean) => void;
  onPaste?: (e: ClipboardEventHandler) => void;
  dataTest?: string;
}

export default memo(function IBAN({
  countryISO,
  onChange,
  dataTest,
  analytics,
}: Props) {
  const [value, setValue] = useState('');
  const [valid, setValid] = useState(false);
  const { track } = useAnalytics();
  const pastedRef = useRef(false);

  useEffect(() => {
    if (!value) {
      setValid(true);
    }
  }, [value]);

  const makeMask = () => {
    const { Code, Length } = countryList.filter(
      (elm) => elm.Code.toUpperCase() === countryISO.toUpperCase(),
    )[0];

    const stars = [];
    for (let i = 0; i < Length; i++) {
      stars.push('*');
    }

    return (
      Code.concat(stars.join('').slice(2))
        .match(/.{1,4}/g)
        ?.join(' ') || ''
    );
  };

  const handleChange = (event: { target: { value: string } }) => {
    const ibanInput = event.target.value.toUpperCase() || '';

    setValue(ibanInput);
    const nextValid = iban.isValid(ibanInput);
    setValid(nextValid);
    if (onChange) onChange(ibanInput, nextValid);

    if (analytics?.dontTrack) {
      return;
    }

    if (pastedRef.current && !nextValid) {
      return;
    }

    if (nextValid || value.includes('_')) {
      track('input', {
        description: analytics?.description,
        value: analytics?.dontTrackContent
          ? NOT_TRACKED_CONTENT
          : analytics?.contentOverride ??
            ibanInput.replace(/\s/g, '').replaceAll('_', ''),
      });
    }
  };

  const handlePaste = (event: any) => {
    const pasteIban = event.clipboardData
      ?.getData('text')
      .toUpperCase()
      .replace(/\s+/g, '');
    const { Code, Length } = countryList.filter(
      (elm) => elm.Code.toUpperCase() === countryISO.toUpperCase(),
    )[0];

    if (pasteIban?.match(`^${Code}.{${Length - Code.length}}$`)) {
      event.preventDefault();
      setValue(pasteIban);
      const nextValid = iban.isValid(pasteIban);
      setValid(nextValid);
      if (onChange) onChange(pasteIban, nextValid);
      if (analytics?.dontTrack) {
        return;
      }

      pastedRef.current = true;

      track(
        'input',
        {
          description: analytics?.description,
          value: analytics?.dontTrackContent
            ? NOT_TRACKED_CONTENT
            : analytics?.contentOverride ??
              pasteIban.replace(/\s/g, '').replaceAll('_', ''),
        },
        {
          isPasted: true,
        },
      );
    }
  };

  return (
    <div className={styles.iban}>
      <InputMask
        mask={value.length ? makeMask() : ''}
        value={value}
        name="IBAN"
        id="IBAN"
        onChange={handleChange}
        onPaste={handlePaste}
        className={classes(
          styles.input,
          layout['full-width-border-box'],
          valid ? styles.success : styles.error,
          valid && !value && styles.natural,
        )}
        placeholder=" "
        data-test={dataTest}
      />
      <label className={styles.label} htmlFor="IBAN">
        Número de cuenta bancaria (IBAN)
      </label>
    </div>
  );
});
